import { en } from './en.js';
import type { TranslationKey } from './en.js';
import { ru } from './ru.js';
import { es } from './es.js';
import { fr } from './fr.js';
import { de } from './de.js';
import { ja } from './ja.js';
import { zh } from './zh.js';
import { ar } from './ar.js';
import { he } from './he.js';

export type { TranslationKey } from './en.js';

export const LOCALES = ['en', 'ru', 'es', 'fr', 'de', 'ja', 'zh', 'ar', 'he'] as const;
export type Locale = (typeof LOCALES)[number];

export type Dictionary = Partial<Record<TranslationKey, string>>;

/** Locales written right-to-left. */
export const RTL_LOCALES: readonly Locale[] = ['ar', 'he'];

export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  ru: 'Русский',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  ja: '日本語',
  zh: '中文',
  ar: 'العربية',
  he: 'עברית',
};

const DICTIONARIES: Record<Locale, Dictionary> = { en, ru, es, fr, de, ja, zh, ar, he };

export type TranslateParams = Record<string, string | number>;

/**
 * Look up a key for a locale, falling back to English and finally to the raw
 * key so the UI never renders `undefined`. `{param}` placeholders are replaced
 * from `params`.
 */
export function translate(locale: Locale, key: TranslationKey, params?: TranslateParams): string {
  const template = DICTIONARIES[locale]?.[key] ?? en[key] ?? key;
  return params ? interpolate(template, params) : template;
}

function interpolate(template: string, params: TranslateParams): string {
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

/** Normalise a BCP-47 language code (e.g. `en-US`, `ZH-hans`) to a supported locale. */
export function resolveLocale(input?: string | null): Locale {
  if (!input) return 'en';
  const base = input.toLowerCase().split(/[-_]/)[0];
  return (LOCALES as readonly string[]).includes(base ?? '') ? (base as Locale) : 'en';
}

export function isRtl(locale: Locale): boolean {
  return RTL_LOCALES.includes(locale);
}

export function dir(locale: Locale): 'rtl' | 'ltr' {
  return isRtl(locale) ? 'rtl' : 'ltr';
}

export type Translator = (key: TranslationKey, params?: TranslateParams) => string;

export function createTranslator(locale: Locale): Translator {
  return (key, params) => translate(locale, key, params);
}
